import { EntitySearchType } from '@api';
import {
  ActionHandlers,
  ChatDocumentContextInput,
  ChatInputConfig,
} from '@terminal/components/chat';
import { useDropdownKeyboardEvents } from '@terminal/hooks/use-dropdown-keyboard-events/useDropdownKeyboardEvents';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useTagsSearch } from '@terminal/hooks/use-tags-search/useTagsSearch';
import {
  EMAIL_TRACKING_PARAM_KEYS,
  queryKeys,
} from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  TrackingMetadata,
} from '@terminal/services/tracking/posthog/constants';
import { PromptRefinerTrackingArgs } from '@terminal/services/tracking/posthog/use-posthog-events/alfredEvents';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { Statistics } from '@terminal/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { useThemeSearch } from '@terminal/widgets/search-asset-widget/hooks/use-theme-search/useThemeSearch';
import clsx from 'clsx';
import React, {
  ForwardRefExoticComponent,
  forwardRef,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ChatActionButtons } from '../../components/chat-action-buttons/ChatActionButtons';
import { ChatInputDropdown } from '../chat-input-dropdown/ChatInputDropdown';
import { UploadDisclaimer } from '../upload-disclaimer/UploadDisclaimer';
import styles from './ChatInputComposer.module.scss';
import { PromptRefinementModal } from './prompt-refinement-modal/PromptRefinementModal';
import { decodeQuestion, isAssetNameMessage } from './utils';

export type PromptRefinementContext = Omit<PromptRefinerTrackingArgs, 'source'>;

export interface ChatInputComposerProps {
  className?: string;
  handlers: ActionHandlers;
  config: ChatInputConfig & {
    isLoading: boolean;
  };
  contextCounts?: {
    statistics?: Statistics;
    documentsCount: number;
    subTypeCount: number;
  };
  trackingMetadata?: TrackingMetadata;
  promptRefinementContext?: PromptRefinementContext;
}

const ChatInputComposerBase = forwardRef<
  HTMLTextAreaElement,
  ChatInputComposerProps
>(
  (
    {
      handlers,
      config,
      contextCounts,
      className,
      trackingMetadata,
      promptRefinementContext,
    }: ChatInputComposerProps,
    ref
  ) => {
    const { t } = useTranslation(['chat']);
    const { params, deleteQueryParam } = useQueryParams();
    const { trackPromptRefinerOpened } = usePosthogEvents();
    const isPromptRefinementEnabled = config.enablePromptRefinement ?? true;
    const refinerTracking: PromptRefinerTrackingArgs = {
      source: trackingMetadata?.source ?? POSTHOG_TRACKING_SOURCES.ALFRED,
      conversationId: promptRefinementContext?.conversationId,
      messageId: promptRefinementContext?.messageId,
    };

    const isAssetMessage = useRef(false);
    const [inputValue, setInputValue] = useState('');
    const [isPromptRefinementOpen, setIsPromptRefinementOpen] = useState(false);
    const [showItems, setShowItems] = useState(true);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const hasPrefilledQuestionRef = useRef(false);
    const { mode } = useChatNavigation();
    const dropdownApiRef = useDropdownKeyboardEvents();
    const { autoFocus: shouldAutoFocus = true } = config;
    const encodedQuestion = params.question;

    // quickAnalysis -- enables asset search + quickAnalysis streaming API
    // quickAnalysis + research -- enables everything, only on dashboard
    // empty -- only question, quickAnalysis streaming API
    const allowedModes = config.allowedModes ?? ['quickAnalysis'];
    const enableAssetSearch =
      mode === 'quickAnalysis' && allowedModes.includes('quickAnalysis');
    const enableThemeSearch =
      enableAssetSearch && !!(config.enableThemeSearch ?? true);

    const {
      tags: tagList,
      searchTags,
      clearTags,
    } = useTagsSearch({
      searchType: EntitySearchType.Entity,
      additionalConditionChecker: isAssetNameMessage,
    });
    const { themeSearchResults, handleTextChange } = useThemeSearch(
      false,
      enableThemeSearch
    );

    useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
      ref,
      () => inputRef.current,
      [inputRef]
    );

    const clearInput = () => {
      setInputValue('');
    };

    const clearDropdown = () => {
      clearTags();
      setShowItems(false);

      if (!config?.persistInputTextOnSubmit) {
        setInputValue('');
      }
    };

    const onSubmitCallbackHandler = () => {
      if (!config?.persistInputTextOnSubmit) {
        clearInput();
      }

      clearDropdown();

      if (inputRef.current) {
        inputRef.current.rows = 1;
      }
    };

    const onChatSubmit = (
      event:
        | React.FormEvent<HTMLFormElement>
        | React.KeyboardEvent<HTMLTextAreaElement>
    ) => {
      event.stopPropagation();
      clearInput();
      handlers.onSubmit(event, inputValue, onSubmitCallbackHandler);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      isAssetMessage.current = isAssetNameMessage(value);
      setInputValue(value);
      setShowItems(true);

      if (enableAssetSearch) {
        searchTags(value);
      }

      if (enableThemeSearch) {
        handleTextChange(value);
      }
    };

    useEffect(() => {
      if (!encodedQuestion) {
        return;
      }

      const question = decodeQuestion(encodedQuestion);
      const paramsToDelete =
        params[queryKeys.utmSource] === POSTHOG_TRACKING_SOURCES.EMAIL
          ? ['question', ...EMAIL_TRACKING_PARAM_KEYS]
          : 'question';

      deleteQueryParam(paramsToDelete, { replace: true });

      if (!question) {
        return;
      }

      hasPrefilledQuestionRef.current = true;
      isAssetMessage.current = isAssetNameMessage(question);
      setInputValue(question);
      setShowItems(true);

      inputRef.current?.focus();
    }, [deleteQueryParam, encodedQuestion, params]);

    const handleSelectRefinedPrompt = (prompt: string) => {
      setIsPromptRefinementOpen(false);
      clearInput();

      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
      } as unknown as React.FormEvent<HTMLFormElement>;

      handlers.onSubmit(syntheticEvent, prompt, onSubmitCallbackHandler, {
        isPromptRefined: true,
      });
    };

    const handleSelectRefinedPrompts = (prompts: string[]) => {
      const selectedPrompts = prompts
        .map(prompt => prompt.trim())
        .filter(Boolean);

      if (selectedPrompts.length === 0) {
        return;
      }

      setIsPromptRefinementOpen(false);
      clearInput();

      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
      } as unknown as React.FormEvent<HTMLFormElement>;

      selectedPrompts.forEach(prompt => {
        handlers.onSubmit(syntheticEvent, prompt, onSubmitCallbackHandler, {
          isPromptRefined: true,
          startNewConversation: true,
        });
      });
    };

    const handleOpenPromptRefinement = () => {
      trackPromptRefinerOpened(refinerTracking);
      setIsPromptRefinementOpen(true);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        if (dropdownApiRef.current?.focusedItemIndex === -1) {
          event.stopPropagation();
          onChatSubmit(event);
        }
      }
    };

    useEffect(() => {
      if (hasPrefilledQuestionRef.current) {
        hasPrefilledQuestionRef.current = false;
        return;
      }

      if (inputRef.current && shouldAutoFocus) {
        inputRef.current.focus();
      } else {
        clearInput();
      }
    }, [inputRef, params.mode, params.conversationId, shouldAutoFocus]);

    const placeholderText = allowedModes.length
      ? t('chat:placeholder', { context: mode })
      : undefined;
    const showPromptRefinement =
      isPromptRefinementEnabled &&
      !!inputValue.trim() &&
      !config.isLoading &&
      !config.isCustomizedDisabled;
    const allowMultiSelect = mode === 'research';

    return (
      <form
        onSubmit={onChatSubmit}
        className={clsx(styles.form, className)}
        data-chat-input-form
      >
        <div
          className={clsx(styles.loaderWrapper, {
            [styles.isMessagePending]: config.isLoading,
          })}
        >
          <ChatInputDropdown
            config={config}
            handlers={{ ...handlers, onSubmitCallbackHandler }}
            inputValue={inputValue}
            tagList={tagList}
            themesList={themeSearchResults}
            clearDropdown={clearDropdown}
            isAssetMessage={isAssetMessage}
            showItems={showItems}
            setInputValue={setInputValue}
            dropdownApiRef={dropdownApiRef}
          >
            <ChatDocumentContextInput
              inputRef={inputRef}
              data-testid="chat-input"
              autoFocus={shouldAutoFocus}
              placeholder={config?.placeholder ?? placeholderText}
              value={inputValue}
              onChange={onInputChange}
              disabled={config.isCustomizedDisabled}
              isMessagePending={config.isLoading}
              contextCounts={contextCounts}
              onKeyDownProp={e => {
                onKeyDown(e);
              }}
              allowedModes={allowedModes}
            >
              <ChatActionButtons
                inputValue={inputValue}
                config={config}
                isMessagePending={config.isLoading}
                abortRequest={handlers.onAbort}
                showRefineButton={showPromptRefinement}
                onRefineClick={handleOpenPromptRefinement}
              />
            </ChatDocumentContextInput>
          </ChatInputDropdown>
        </div>
        <div className={styles.footer}>
          <UploadDisclaimer />
        </div>
        {isPromptRefinementEnabled && (
          <PromptRefinementModal
            isOpen={isPromptRefinementOpen}
            question={inputValue}
            onClose={() => setIsPromptRefinementOpen(false)}
            onSelectPrompt={handleSelectRefinedPrompt}
            onSelectPrompts={handleSelectRefinedPrompts}
            onUpdateQuestion={setInputValue}
            tracking={refinerTracking}
            allowMultiSelect={allowMultiSelect}
          />
        )}
      </form>
    );
  }
);

export const ChatInputComposer: ForwardRefExoticComponent<
  ChatInputComposerProps & RefAttributes<HTMLTextAreaElement>
> = ChatInputComposerBase;
